// created by AI
import { requestApi2_getError } from "@utils/http"
import type { GetTimeseriesDataArgs, TimeseriesData } from "api"
import { endpoints, TIMESERIES_REQUEST_MAX_DAYS } from "api"

const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000
const MAX_BATCH_DURATION_MILLISECONDS = TIMESERIES_REQUEST_MAX_DAYS * ONE_DAY_IN_MILLISECONDS

type TimeseriesRequester = (
	args: GetTimeseriesDataArgs,
	abortSignal?: AbortSignal
) => Promise<TimeseriesData | null>

/**
 * Fetches a timeseries interval through sequential, endpoint-sized requests and merges the results.
 * Sequential requests prevent a page with several plots from multiplying database load by the number
 * of interval batches. Aggregation is copied unchanged to every batch so resolution remains based on
 * the complete range selected by the user.
 *
 * @param args - Complete interval, sensors, and optional aggregation requested by the caller.
 * @param abortSignal - Signal used to cancel every remaining batch when the query becomes obsolete.
 * @param request - Injectable transport used by unit tests; production uses the portal endpoint.
 * @returns Timeseries data ordered by requested sensor and timestamp.
 */
export async function fetchTimeseriesDataInBatches(
	args: GetTimeseriesDataArgs,
	abortSignal?: AbortSignal,
	request: TimeseriesRequester = async (requestArgs, signal) =>
		requestApi2_getError(endpoints.getTimeseriesData4, requestArgs, signal)
): Promise<TimeseriesData> {
	// created by AI
	const intervals = splitTimeseriesInterval(args)
	const dataBySensorId = new Map<number, Map<number, [Date, number]>>()

	for (const interval of intervals) {
		const response = await request({ ...args, interval }, abortSignal)
		if (response === null) {
			throw new Error("The timeseries endpoint returned no response")
		}

		for (const sensor of response.sensors) {
			let dataByTimestamp = dataBySensorId.get(sensor.id)
			if (dataByTimestamp === undefined) {
				dataByTimestamp = new Map<number, [Date, number]>()
				dataBySensorId.set(sensor.id, dataByTimestamp)
			}
			for (const dataPoint of sensor.data) {
				// A batch includes the latest point before its start for carry-forward calculations.
				// Keep the earlier batch's complete aggregation bucket when timestamps overlap.
				if (!dataByTimestamp.has(dataPoint[0].getTime())) {
					dataByTimestamp.set(dataPoint[0].getTime(), dataPoint)
				}
			}
		}
	}

	return {
		sensors: args.sensorIds.map((id) => ({
			id,
			data: Array.from(dataBySensorId.get(id)?.values() ?? []).sort(
				(left, right) => left[0].getTime() - right[0].getTime()
			),
		})),
	}
}

/**
 * Divides a complete interval into half-open endpoint-sized intervals.
 * Internal boundaries follow aggregation buckets so no hour or minute is calculated partially by
 * two different requests.
 *
 * @param args - Request whose interval and aggregation determine the batch boundaries.
 * @returns Contiguous intervals, each no longer than the endpoint maximum.
 */
export function splitTimeseriesInterval(
	args: GetTimeseriesDataArgs
): GetTimeseriesDataArgs["interval"][] {
	// created by AI
	const { gte, lt } = args.interval
	if (gte.getTime() >= lt.getTime()) {
		return [{ gte, lt }]
	}

	const intervals: GetTimeseriesDataArgs["interval"][] = []
	let batchStart = gte
	while (batchStart.getTime() < lt.getTime()) {
		const maximumEnd = new Date(batchStart.getTime() + MAX_BATCH_DURATION_MILLISECONDS)
		let batchEnd = maximumEnd.getTime() < lt.getTime() ? maximumEnd : lt

		if (batchEnd !== lt && args.aggregation !== undefined) {
			batchEnd = alignDownToAggregationBucket(batchEnd, args.aggregation.groupBy)
		}
		intervals.push({ gte: batchStart, lt: batchEnd })
		batchStart = batchEnd
	}
	return intervals
}

/** Aligns an internal boundary to the start of its UTC aggregation bucket. */
function alignDownToAggregationBucket(date: Date, groupBy: "hour" | "minute"): Date {
	// created by AI
	const aligned = new Date(date)
	aligned.setUTCSeconds(0, 0)
	if (groupBy === "hour") {
		aligned.setUTCMinutes(0)
	}
	return aligned
}
